/**
 * @fileoverview The plants growing at one location, as rows to open.
 *
 * A row opens the plant here rather than navigating to the garden and
 * scrolling to it: the same card, over this page, the way a tile opens on the
 * plant list. Going to find out about a plant should not cost you the
 * location you were reading.
 *
 * Each row is the plant's photo (its first, from the garden; a sprout where
 * it has none), its one name (the backend's names, services/grow/plantNaming;
 * its kind is not said under it), its stage, how its week is going
 * (Thriving, Stressed, At risk, as its card says it; none where the plants
 * brief sends none, as for a sheltered location, which the weather does not
 * reach: the server's rule, audit batch 4, A23), and a › to say it opens. It
 * was a name in grey, with nothing to say it could be tapped.
 *
 * Where: LocationCard, under Plants.
 * API: none of its own: the garden (usePlantsContext) for each plant's
 * photo and stage, the plants brief (usePlantsBrief) for its week.
 * Words (en/es): locations.card.plants.list.empty; the week's words
 * (WeekStateLabel).
 *
 * @module LocationPlantList
 */

import React from 'react';
import { ChevronRight, Sprout } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { usePlantsContext } from '@/shared/hooks/usePlantsContext';
import { usePlantsBrief } from '@/shared/hooks/usePlantsBrief';
import type { LocationPlant } from '@/shared/api/locations';
import { plantCoverUrl } from '@/features/plant-list/lib/plantPhotoUrl';
import { WeekStateLabel } from '@/features/plant-list/components/card/WeekState';
import { FramedPhoto } from '@/shared/components/ui/FramedPhoto';

/** The plants, and what opening one does. */
interface LocationPlantListProps {
  /** The bed's plants growing now, in the bed summary's thin shape. */
  plants: LocationPlant[];
  /** Opens one plant full size, over this page. */
  onOpenPlant: (plantObjId: number) => void;
}

/**
 * The rows.
 *
 * @param props - LocationPlantListProps.
 */
export const LocationPlantList: React.FC<LocationPlantListProps> = ({ plants, onOpenPlant }) => {
  const { reactTranslate: t } = useTranslation();
  const { plants: garden } = usePlantsContext();
  const { forPlant } = usePlantsBrief();
  // The garden's own plants, for each row's photo: a location's summary has none.
  /** The garden's plants by id. */
  const byId = React.useMemo(() => new Map((garden ?? []).map((plant) => [plant.plant_obj_id, plant])), [garden]);

  if (plants.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('locations.card.plants.list.empty')}</p>;
  }

  return (
    <ul className="divide-y">
      {plants.map((plant) => {
        /** The garden's own plant: its photo and its stage. */
        const full = byId.get(plant.plant_obj_id);
        // Its stage as the server sends it on the garden's plant (W24 Job F); the bed's summary carries an old one, always empty.
        const stage = full?.stage?.name;
        // The plant's one name (the backend's names, services/grow/plantNaming); its kind is not said under it.
        const name = plant.names.full;
        const cover = full ? plantCoverUrl(full, 'thumb') : '';
        // The brief's week, or none: it sends none for a sheltered bed or a plant at Planning.
        const week = forPlant(plant.plant_obj_id)?.week ?? null;
        return (
          <li key={plant.plant_obj_id}>
            <button
              type="button"
              onClick={() => onOpenPlant(plant.plant_obj_id)}
              className="flex min-h-14 w-full items-center gap-3 rounded-sm px-2 py-1.5 text-left transition-colors hover:bg-muted/60"
            >
              {cover ? (
                <span className="relative h-10 w-10 shrink-0 overflow-hidden rounded-md"><FramedPhoto src={cover} alt="" loading="lazy" /></span>
              ) : (
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground" aria-hidden="true">
                  <Sprout className="h-4 w-4" />
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="line-clamp-2 text-[15px] font-semibold leading-snug font-display">
                  {name}
                </span>
                {stage && (
                  <span className="block truncate text-xs text-muted-foreground">{stage}</span>
                )}
              </span>
              {week && (
                <WeekStateLabel status={{ state: week.state, token: week.token }} className="shrink-0 rounded-full px-2 py-0.5 text-xs" />
              )}
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            </button>
          </li>
        );
      })}
    </ul>
  );
};
